import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Alert, Card, Col, Form, Row, Space, Spin, Tag, Typography
} from 'antd';
import {
    BookOutlined, FileTextOutlined
} from '@ant-design/icons';
import { useRoute, useLocation } from 'wouter';
import { getIcon } from './utils/iconMap';
import * as lessonPlanApi from './services/lessonPlanApi';
import { useBreadcrumbComponent } from '@common/useBreadcrumbComponent';
import PageHeader from "@common/PageHeader";
import Button from "@common/Button";
import {DownloadRounded, EditRounded} from "@mui/icons-material";

// Import extracted components
import BlockLibraryModal from "./components/BlockLibraryModal";
import SegmentEditModal from "./components/SegmentEditModal";
import LessonConfigurationCard from "./components/LessonConfigurationCard";
import TeachersNote from "./components/TeachersNote";
import LearningObjectivesCard from "./components/LearningObjectivesCard";
import InstructionalPlanSection from "./components/InstructionalPlanSection";
import ExitQuizCard from "./components/ExitQuizCard";
import DifferentiationCard from "./components/DifferentiationCard";
import ProficiencyRubricCard from "./components/ProficiencyRubricCard";
import ProbingQuestionsCard from "./components/ProbingQuestionsCard";
import LanguageScaffoldsCard from "./components/LanguageScaffoldsCard";
import PlanningConsiderationsSection from "./components/PlanningConsiderationsSection";
import PageAnchorNav from "./components/PageAnchorNav";

const { Text } = Typography;


const updateNestedState = (obj, path, value) => {
    const newObj = JSON.parse(JSON.stringify(obj)); // Deep copy
    const keys = path.split('.').map(key => isNaN(parseInt(key, 10)) ? key : parseInt(key, 10));
    let current = newObj;
    for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
        if (current === undefined) return obj; // Path does not exist
    }
    current[keys[keys.length - 1]] = value;
    return newObj;
};

const formatConfigKey = (key) => {
    if (typeof key !== 'string') return key;
    return key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
};


const InteractiveLessonPlanDevelopment = () => {
    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: false, dropEndingSeparator: true });

    // Get planId from route and location for redirects
    const [match, params] = useRoute('/web/ui/view/lessonPlans/plans/:planId');
    const [location, setLocation] = useLocation();
    const planId = params?.planId;

    // Data state
    const [formValues, setFormValues] = useState({});
    const [lessonContent, setLessonContent] = useState(null);
    const [instructionalBlocks, setInstructionalBlocks] = useState([]);
    const [wizardConfig, setWizardConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI state
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showNewPlanBanner, setShowNewPlanBanner] = useState(false);
    const [editingState, setEditingState] = useState({ key: null, content: '' });
    const [adaptedKeys, setAdaptedKeys] = useState(new Set());
    const [isBlockLibraryVisible, setIsBlockLibraryVisible] = useState(false);
    const [insertionIndex, setInsertionIndex] = useState(null);
    const [isSegmentModalVisible, setIsSegmentModalVisible] = useState(false);
    const [editingSegmentIndex, setEditingSegmentIndex] = useState(null);
    const [segmentForm] = Form.useForm();

    const getScrollContainer = () => {
        return document.getElementById('contentPanel');
    };

    // Fetch lesson plan data when planId changes
    useEffect(() => {
        const fetchLessonData = async () => {
            if (!planId) {
                displayToastMessage('No lesson plan ID provided', null, 'error', null, 10000);
                setLocation('/web/ui/view/lessonPlans');
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Fetch wizard config and lesson plan details in parallel
                const [config, fullLessonPlan] = await Promise.all([
                    lessonPlanApi.getWizardConfig(),
                    lessonPlanApi.getLessonPlanDetails(planId)
                ]);

                if (!fullLessonPlan.lessonPlanContent) {
                    throw new Error('Lesson plan content is missing.');
                }

                setWizardConfig(config);
                setFormValues(fullLessonPlan.formValues || {});
                setLessonContent(fullLessonPlan.lessonPlanContent);
                setAdaptedKeys(new Set());

                // Fetch template-specific instructional blocks if available
                if (fullLessonPlan.formValues?.lessonPlanTemplateId) {
                    try {
                        const templateBlocks = await lessonPlanApi.getInstructionalBlocks(
                            fullLessonPlan.formValues.lessonPlanTemplateId
                        );
                        setInstructionalBlocks(templateBlocks);
                    } catch (blocksErr) {
                        console.warn(`Failed to fetch template-specific blocks: ${blocksErr.message}. Falling back to generic blocks.`);
                        setInstructionalBlocks([]);
                    }
                } else {
                    setInstructionalBlocks([]);
                }
            } catch (err) {
                const errorMessage = err.message || 'Failed to load lesson plan.';
                displayToastMessage(errorMessage, null, 'error', null, 10000);
                // Redirect to base URL on error (plan not found, etc.)
                setLocation('/web/ui/view/lessonPlans');
            } finally {
                setIsLoading(false);
            }
        };

        fetchLessonData();
    }, [planId, setLocation]);

    // Check for editPlan and newPlan query parameters
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const hasEditPlan = searchParams.get('editPlan') === 'true';
        const hasNewPlan = searchParams.get('newPlan') === 'true';

        if (hasEditPlan || hasNewPlan) {
            setIsEditMode(true);

            if (hasNewPlan) {
                setShowNewPlanBanner(true);
            }

            // Remove the query params
            searchParams.delete('editPlan');
            searchParams.delete('newPlan');
            const newSearch = searchParams.toString();
            const newUrl = location.split('?')[0] + (newSearch ? `?${newSearch}` : '');
            setLocation(newUrl, { replace: true });
        }
    }, [location, setLocation]);

    const allContentTypes = useMemo(() => {
        const contentTypes = wizardConfig?.contentTypes || {};
        return [
            ...(contentTypes.instructional || []),
            ...(contentTypes.practice || []),
            ...(contentTypes.assessment || [])
        ];
    }, [wizardConfig]);


    // --- EditableSectionDevelopment Handlers (Unchanged) ---
    const handleEdit = (key, content) => setEditingState({ key, content });
    const handleContentChange = (content) => setEditingState(prev => ({ ...prev, content }));
    const handleCancel = () => setEditingState({ key: null, content: '' });

    const handleSave = () => {
        setLessonContent(prev => updateNestedState(prev, editingState.key, editingState.content));
        setAdaptedKeys(prev => new Set(prev).add(editingState.key));
        setEditingState({ key: null, content: '' });
    };

    const handleSaveAdaptations = async () => {
        if (!planId) {
            displayToastMessage("Cannot save: No lesson ID provided.", null, 'error', null, 10000);
            return;
        }

        setIsSaving(true);
        try {
            await lessonPlanApi.updateLessonPlan(planId, lessonContent);
            displayToastMessage('Adaptations saved!', null, 'success', null, 5000);
            setAdaptedKeys(new Set());
            setIsEditMode(false);
        } catch (err) {
            displayToastMessage(`Save failed: ${err.message}`, null, 'error', null, 10000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleEditMode = () => {
        if (isEditMode) {
            // Switching from Edit to View mode - save changes
            handleSaveAdaptations();
        } else {
            // Switching from View to Edit mode
            setIsEditMode(true);
        }
    };

    const handleExport = async () => {
        if (!planId) {
            displayToastMessage("Cannot export: No lesson ID provided.", null, 'error', null, 10000);
            return;
        }

        try {
            const { blob, filename } = await lessonPlanApi.downloadWordDocument(planId);

            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            displayToastMessage('Lesson plan exported successfully!', null, 'success', null, 5000);
        } catch (error) {
            displayToastMessage(`Export failed: ${error.message}`, null, 'error', null, 10000);
        }
    };

    // --- Segment & Block Management Handlers ---
    const showBlockLibrary = (index) => {
        setInsertionIndex(index);
        setIsBlockLibraryVisible(true);
    };

    const handleAddBlock = (block) => {
        const newSegment = {
            name: block.title,
            minutes: 10,
            goal: block.description || 'Custom activity',
            teacherMoves: ['Facilitate the new activity.'],
            studentActions: ['Engage in the activity.'],
            checksForUnderstanding: ['Monitor student participation.'],
            differentiation: null,
            materials: [],
            textbookResources: []
        };

        const updatedSegments = [...lessonContent.segments];
        updatedSegments.splice(insertionIndex, 0, newSegment);

        setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
        setIsBlockLibraryVisible(false);
        setInsertionIndex(null);
        setAdaptedKeys(prev => new Set(prev).add(`segments.${insertionIndex}`));
        displayToastMessage(`"${block.title}" block added to your plan.`, null, 'success', null, 5000);
    };

    const handleRemoveSegment = (indexToRemove) => {
        Modal.confirm({
            title: 'Remove Segment?',
            content: `Are you sure you want to remove "${lessonContent.segments[indexToRemove].name}"?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: () => {
                const updatedSegments = lessonContent.segments.filter((_, i) => i !== indexToRemove);
                setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
                setAdaptedKeys(prev => new Set(prev).add(`segments`));
                displayToastMessage('Segment removed.', null, 'success', null, 5000);
            }
        });
    };

    const handleRemoveResource = (segmentIndex, resourceIndex) => {
        const resource = lessonContent.segments[segmentIndex].textbookResources[resourceIndex];
        Modal.confirm({
            title: 'Remove Resource?',
            content: `Are you sure you want to remove "${resource.displayTitle}" from this segment?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: () => {
                const updatedSegments = [...lessonContent.segments];
                const segmentToUpdate = { ...updatedSegments[segmentIndex] };

                const updatedResources = segmentToUpdate.textbookResources.filter((_, i) => i !== resourceIndex);

                segmentToUpdate.textbookResources = updatedResources;
                updatedSegments[segmentIndex] = segmentToUpdate;

                setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
                setAdaptedKeys(prev => new Set(prev).add(`segments.${segmentIndex}.textbookResources`));
                displayToastMessage('Resource removed.', null, 'success', null, 5000);
            }
        });
    };

    const handleAddListItem = (path, newItemTemplate, itemName = 'New item') => {
        const newLessonContent = JSON.parse(JSON.stringify(lessonContent));

        const keys = path.split('.');
        let current = newLessonContent;
        let parent = null;
        let lastKey = null;

        for (const key of keys) {
            if (current[key] === undefined || current[key] === null) {
                if (key === keys[keys.length - 1]) {
                    current[key] = [];
                } else {
                    current[key] = {};
                }
            }
            parent = current;
            current = current[key];
            lastKey = key;
        }

        if (!Array.isArray(current)) {
            console.error("Path does not lead to an array:", path);
            if (Array.isArray(parent[lastKey])) {
                current = parent[lastKey];
            } else {
                displayToastMessage("Could not add item: not an array.", null, 'error', null, 10000);
                return;
            }
        }

        const newItem = typeof newItemTemplate === 'function'
            ? newItemTemplate()
            : JSON.parse(JSON.stringify(newItemTemplate));

        current.push(newItem);

        setLessonContent(newLessonContent);
        setAdaptedKeys(prev => new Set(prev).add(path));
        displayToastMessage(`${itemName} added.`, null, 'success', null, 5000);
    };

    const handleRemoveListItem = (path, indexToRemove, itemText = 'this item') => {
        Modal.confirm({
            title: 'Remove Item?',
            content: `Are you sure you want to remove "${itemText.substring(0, 100)}${itemText.length > 100 ? '...' : ''}"?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: () => {
                const newLessonContent = JSON.parse(JSON.stringify(lessonContent));

                const keys = path.split('.');
                let current = newLessonContent;

                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    if (current[key] === undefined) {
                        console.error("Path not found for deletion:", path);
                        displayToastMessage("Could not remove item: path not found.", null, 'error', null, 10000);
                        return;
                    }
                    current = current[key];
                }

                if (!Array.isArray(current)) {
                    console.error("Path does not lead to an array:", path);
                    displayToastMessage("Could not remove item: not an array.", null, 'error', null, 10000);
                    return;
                }

                current.splice(indexToRemove, 1);

                setLessonContent(newLessonContent);
                setAdaptedKeys(prev => new Set(prev).add(path));
                displayToastMessage(`Item removed.`, null, 'success', null, 5000);
            }
        });
    };

    const handleClearField = (path, fieldName = 'this field') => {
        Modal.confirm({
            title: `Clear ${fieldName}?`,
            content: `Are you sure you want to clear the content for ${fieldName}?`,
            okText: 'Clear',
            okType: 'danger',
            onOk: () => {
                const newLessonContent = updateNestedState(lessonContent, path, null);

                setLessonContent(newLessonContent);
                setAdaptedKeys(prev => new Set(prev).add(path));
                displayToastMessage(`${fieldName} cleared.`, null, 'success', null, 5000);
            }
        });
    };


    const showSegmentEditModal = (index) => {
        setEditingSegmentIndex(index);
        segmentForm.setFieldsValue(lessonContent.segments[index]);
        setIsSegmentModalVisible(true);
    };

    const handleSegmentModalSave = () => {
        segmentForm.validateFields().then(values => {
            const updatedSegments = [...lessonContent.segments];
            updatedSegments[editingSegmentIndex] = {
                ...updatedSegments[editingSegmentIndex],
                ...values
            };
            setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
            setIsSegmentModalVisible(false);
            setEditingSegmentIndex(null);
            setAdaptedKeys(prev => new Set(prev).add(`segments.${editingSegmentIndex}`));
            displayToastMessage('Segment updated.', null, 'success', null, 5000);
        });
    };

    const getResourceIcon = useCallback((type) => {
        const lowerType = type ? type.toLowerCase() : 'unknown';
        let mappedType = lowerType;

        switch(lowerType) {
            case 'practice_question':
                mappedType = 'quiz';
                break;
            case 'lesson_notes':
                mappedType = 'lesson_notes';
                break;
            case 'video':
                mappedType = 'video';
                break;
            case 'worksheet':
                mappedType = 'worksheet';
                break;
            case 'scorcher':
                mappedType = 'scorcher';
                break;
            case 'quiz':
                mappedType = 'quiz';
                break;
            case 'question':
                mappedType = 'quiz';
                break;
            default:
                mappedType = lowerType;
        }

        const resource = allContentTypes.find(t => t.value === mappedType);
        return resource ? React.cloneElement(getIcon(resource.icon), { style: { fontSize: '22px', color: '#1677ff' } }) :
            <FileTextOutlined style={{ fontSize: '22px', color: '#1677ff' }} />;
    }, [allContentTypes]);


    const resourceLabelMap = useMemo(() => {
        const map = new Map();
        allContentTypes.forEach(item => {
            map.set(item.value, item.label);
        });
        return map;
    }, [allContentTypes]);

    // --- useMemo hook (Unchanged) ---
    const configData = useMemo(() => {
        if (!formValues || Object.keys(formValues).length === 0) return [];

        const data = [
            { title: 'Learning Goal', value: formValues.learningGoal },
            { title: 'Knowledge Dimension', value: formValues.knowledgeDimension },
            { title: 'Timeframe', value: formValues.timeframe },
            { title: 'DOK Level', value: formValues.dokLevel },
            { title: 'Delivery Mode', value: formValues.classroomFormat },
            { title: 'Resource Profile', value: formValues.resourceProfile },
            { title: 'Assessment', value: formValues.assessmentFitCategory },
            { title: 'Well-being Focus', value: formValues.wellBeingFocus },
            { title: 'Lesson Plan Template', value: formValues.lessonPlanTemplateName || <Text code>{formValues.lessonPlanTemplateId}</Text> },
        ].filter(item => item.value);

        if (formValues.syllabusName || formValues.topicName || formValues.conceptName) {
            data.unshift({
                title: 'Aligned Textbook Section',
                value: (
                    <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                        {formValues.syllabusName && (
                            <Text><BookOutlined /> {formValues.syllabusName}</Text>
                        )}
                        {formValues.topicName && (
                            <Text>{formValues.topicName}</Text>
                        )}
                        {formValues.conceptName && (
                            <Text>{formValues.conceptName}</Text>
                        )}
                    </Space>
                )
            });
        }

        if (formValues.selectedResources?.length > 0) {
            data.push({
                title: 'Selected Resources',
                value: (
                    <Space wrap>
                        {formValues.selectedResources.map(res => (
                            <Tag key={res} icon={React.cloneElement(getResourceIcon(res), {style: {fontSize: '14px', marginRight: '4px'}})}>
                                {resourceLabelMap.get(res) || formatConfigKey(res)}
                            </Tag>
                        ))}
                    </Space>
                )
            });
        }

        if (formValues.lessonPlanComponents?.length > 0) {
            data.push({
                title: 'Lesson Components',
                value: (
                    <Space wrap>
                        {formValues.lessonPlanComponents.map(comp => (
                            <Tag key={comp} color="blue">{formatConfigKey(comp)}</Tag>
                        ))}
                    </Space>
                )
            });
        }

        return data;
    }, [formValues, resourceLabelMap, getResourceIcon]);


    const showComponents = useMemo(() => {
        const components = new Set(formValues?.lessonPlanComponents || []);
        if (lessonContent?.exitQuizQuestions?.length > 0) {
            components.add('exit_quiz');
        }
        return components;
    }, [formValues, lessonContent]);


    // Loading state
    if (isLoading) {
        return (
            <Card style={{ minHeight: 400, textAlign: 'center', padding: '100px 0' }}>
                <Spin description="Loading lesson plan..." size="large" />
            </Card>
        );
    }

    // Error state
    // Replace this with a redirect and a message
    if (error) {
        return (
            <Card style={{ minHeight: 400, padding: '40px' }}>
                <Alert
                    message="Error Loading Lesson Plan"
                    description={error}
                    type="error"
                    showIcon
                />
            </Card>
        );
    }

    // No content state
    // Don't see why we would need this
    if (!lessonContent || !lessonContent.segments) {
        return (
            <Card style={{ minHeight: 400, padding: '40px' }}>
                <Alert
                    message="No Content"
                    description="This lesson plan has no content."
                    type="warning"
                    showIcon
                />
            </Card>
        );
    }

    const showPlanningBlock = (
        showComponents.has('corrective_plan') ||
        showComponents.has('pitfalls_indicators') ||
        showComponents.has('planning_notes')
    );

    const anchorLinks = [];
    anchorLinks.push({ key: 'objectives', href: '#lesson-objectives', title: 'Learning Objectives' });

    if (configData.length > 0) {
        anchorLinks.push({ key: 'config', href: '#lesson-configuration', title: 'Lesson Plan Configuration' });
    }

    anchorLinks.push({ key: 'plan', href: '#lesson-plan', title: 'Instructional Plan' });

    if (showComponents.has('exit_quiz')) {
        anchorLinks.push({ key: 'exitquiz', href: '#lesson-exit-quiz', title: 'Exit Quiz' });
    }

    if (showComponents.has('overview')) {
        anchorLinks.push({ key: 'diff', href: '#lesson-differentiation', title: 'Differentiation' });
    }
    if (showComponents.has('rubric')) {
        anchorLinks.push({ key: 'rubric', href: '#lesson-rubric', title: 'Proficiency Rubric' });
    }
    if (showComponents.has('probing_questions')) {
        anchorLinks.push({ key: 'qs', href: '#lesson-questions', title: 'Probing Questions' });
    }
    if (showComponents.has('language_scaffolds')) {
        anchorLinks.push({ key: 'lang', href: '#lesson-language', title: 'Language Scaffolds' });
    }
    if (showPlanningBlock) {
        anchorLinks.push({ key: 'planning', href: '#lesson-planning', title: 'Planning Considerations' });
    }

    return (
        <>
            <BreadcrumbBar />
            <PageHeader
                title={lessonContent.contentName}
                subtitle={lessonContent.lessonName}
                buttons={
                    <>
                        <Button
                            onClick={handleExport}
                            type={'outline'}
                            icon={<DownloadRounded />}
                        >
                            Export Plan
                        </Button>
                        <Button
                            onClick={handleToggleEditMode}
                            type={isEditMode ? 'primary' : 'outline'}
                            icon={<EditRounded />}
                            loading={isSaving}
                            disabled={isSaving}
                        >
                            {isEditMode ? 'Save' : 'Edit'}
                        </Button>
                    </>
                }
                sticky={true}
            />
            <Row gutter={24}>
                <Col sm={24} md={19}>
                    {/* Modals */}
                    <BlockLibraryModal
                        visible={isBlockLibraryVisible}
                        onCancel={() => setIsBlockLibraryVisible(false)}
                        instructionalBlocks={instructionalBlocks}
                        onAddBlock={handleAddBlock}
                    />
                    <SegmentEditModal
                        visible={isSegmentModalVisible}
                        onOk={handleSegmentModalSave}
                        onCancel={() => setIsSegmentModalVisible(false)}
                        form={segmentForm}
                    />

                    <div style={{ width: '100%', maxWidth: '100ch', margin: '0 auto' }}>
                        <Space orientation="vertical" style={{ width: '100%' }} size="large">
                            {showNewPlanBanner && (
                                <Alert
                                    description="This is your generated plan. Edit and add sections and information to your new plan below. Once you are happy, select the Save button to finalise the process. You can always come back to your plan from the main Lesson Plans screen."
                                    type="info"
                                    showIcon
                                    closable
                                    onClose={() => setShowNewPlanBanner(false)}
                                />
                            )}
                            <LearningObjectivesCard
                                objectives={lessonContent.objectives}
                                isEditMode={isEditMode}
                                editingState={editingState}
                                adaptedKeys={adaptedKeys}
                                handleEdit={handleEdit}
                                handleContentChange={handleContentChange}
                                handleSave={handleSave}
                                handleCancel={handleCancel}
                                handleAddListItem={handleAddListItem}
                                handleRemoveListItem={handleRemoveListItem}
                            />
                            <LessonConfigurationCard configData={configData} defaultOpen={showNewPlanBanner} />

                            <TeachersNote
                                customInstructions={formValues?.customInstructions || lessonContent?.customInstructions}
                            />

                            <InstructionalPlanSection
                                segments={lessonContent.segments}
                                totalMinutes={lessonContent.minutes}
                                isEditMode={isEditMode}
                                editingState={editingState}
                                adaptedKeys={adaptedKeys}
                                handleEdit={handleEdit}
                                handleContentChange={handleContentChange}
                                handleSave={handleSave}
                                handleCancel={handleCancel}
                                handleAddListItem={handleAddListItem}
                                handleRemoveListItem={handleRemoveListItem}
                                handleRemoveSegment={handleRemoveSegment}
                                handleRemoveResource={handleRemoveResource}
                                showSegmentEditModal={showSegmentEditModal}
                                showBlockLibrary={showBlockLibrary}
                                getResourceIcon={getResourceIcon}
                            />

                            {showComponents.has('exit_quiz') && (
                                <ExitQuizCard
                                    exitQuizQuestions={lessonContent.exitQuizQuestions}
                                    isEditMode={isEditMode}
                                    getResourceIcon={getResourceIcon}
                                    handleRemoveListItem={handleRemoveListItem}
                                />
                            )}

                            {showComponents.has('overview') && (
                                <DifferentiationCard
                                    differentiationBrief={lessonContent.differentiationBrief}
                                    isEditMode={isEditMode}
                                    editingState={editingState}
                                    adaptedKeys={adaptedKeys}
                                    handleEdit={handleEdit}
                                    handleContentChange={handleContentChange}
                                    handleSave={handleSave}
                                    handleCancel={handleCancel}
                                    handleClearField={handleClearField}
                                />
                            )}

                            {showComponents.has('rubric') && (
                                <ProficiencyRubricCard
                                    proficiencyRubric={lessonContent.proficiencyRubric}
                                    isEditMode={isEditMode}
                                    editingState={editingState}
                                    adaptedKeys={adaptedKeys}
                                    handleEdit={handleEdit}
                                    handleContentChange={handleContentChange}
                                    handleSave={handleSave}
                                    handleCancel={handleCancel}
                                    handleAddListItem={handleAddListItem}
                                    handleRemoveListItem={handleRemoveListItem}
                                    handleClearField={handleClearField}
                                />
                            )}

                            {showComponents.has('probing_questions') && (
                                <ProbingQuestionsCard
                                    keyProbingQuestions={lessonContent.keyProbingQuestions}
                                    isEditMode={isEditMode}
                                    editingState={editingState}
                                    adaptedKeys={adaptedKeys}
                                    handleEdit={handleEdit}
                                    handleContentChange={handleContentChange}
                                    handleSave={handleSave}
                                    handleCancel={handleCancel}
                                    handleAddListItem={handleAddListItem}
                                    handleRemoveListItem={handleRemoveListItem}
                                />
                            )}

                            {showComponents.has('language_scaffolds') && (
                                <LanguageScaffoldsCard
                                    academicLanguageScaffolds={lessonContent.academicLanguageScaffolds}
                                    isEditMode={isEditMode}
                                    editingState={editingState}
                                    adaptedKeys={adaptedKeys}
                                    handleEdit={handleEdit}
                                    handleContentChange={handleContentChange}
                                    handleSave={handleSave}
                                    handleCancel={handleCancel}
                                    handleAddListItem={handleAddListItem}
                                    handleRemoveListItem={handleRemoveListItem}
                                    handleClearField={handleClearField}
                                />
                            )}

                            {showPlanningBlock && (
                                <PlanningConsiderationsSection
                                    lessonContent={lessonContent}
                                    showComponents={showComponents}
                                    isEditMode={isEditMode}
                                    editingState={editingState}
                                    adaptedKeys={adaptedKeys}
                                    handleEdit={handleEdit}
                                    handleContentChange={handleContentChange}
                                    handleSave={handleSave}
                                    handleCancel={handleCancel}
                                    handleAddListItem={handleAddListItem}
                                    handleRemoveListItem={handleRemoveListItem}
                                    handleClearField={handleClearField}
                                />
                            )}
                        </Space>
                    </div>
                </Col>

                {/* Sticky Sidebar */}
                <Col sm={24} md={5}>
                    <PageAnchorNav
                        anchorLinks={anchorLinks}
                        getScrollContainer={getScrollContainer}
                    />
                </Col>
            </Row>
        </>
    );
};

export default InteractiveLessonPlanDevelopment;